// src/components/healthrecords/EffectivePanelTab.jsx
//
// A patient's CURRENT marker picture, assembled from every draw on file —
// not just whichever triage ran most recently. Backed by
// GET /api/patients/{patientId}/effective-panel (V76 Phase 2,
// EffectivePanelAssembler), which classifies each canonical marker as:
//
//   fresh        — within its validity window, usable as current
//   stale        — a real value exists, it has just aged out (shown, not hidden)
//   never tested — zero observations exist, ever, for that registry code
//
// The three-way split is the point: a two-marker follow-up used to make a
// patient's other 30+ markers look silently "fine" simply because they
// weren't in that document. This view makes the gap visible instead.
//
// `recommendations` (added alongside the fresh/stale/neverTested split) is a
// narrower, actionable subset of stale + never-tested: only markers
// EffectivePanelAssembler has a clinically reviewed retest window for.
// Acute markers, gender-split ALT/GGT, and anything without a reviewed
// window never appear there even if they're stale or never-tested — see
// EffectivePanelAssembler.buildRecommendations's Javadoc.
//
// `onLoaded` (optional) receives {current, attention, areas} once the panel
// loads, for a page-level stat strip.
//
// `patientView` (patient portal only — see PatientCurrentMarkersPage.jsx)
// drops the standalone Stale and Never Tested cards entirely and renders
// the remaining two — Current and Recommended Retests — as tabs (defaulting
// to Current) instead of stacked cards. A patient reading "Stale (4)" and
// "Never Tested (9)" right next to "Recommended Retests (6)" read those as
// three different problems rather than one — Recommended IS the actionable
// subset of exactly those two buckets, just narrower (it excludes acute
// markers and anything without a reviewed retest window). The navigator and
// doctor portals show three tabs — Current Markers, Recommended, Stale
// and Never Tested — so a clinician still reaches every
// stale/untested marker, not just the ones with a defined retest cadence.
//
// Read-only. Does not affect, and is not affected by, any risk score shown
// elsewhere on this patient — see EffectivePanelAssembler's own Javadoc for
// why that separation is deliberate at this stage.

import React, { useEffect, useState, useCallback } from 'react';
import { effectivePanelAPI } from '../../services/api';
import { markerStatusBadgeClass, markerStatusLabel } from '../../utils/markerStatus';
import { matchMarkerHistory } from '../../utils/markerHistory';
import Icon from '../Icon';
import Sparkline from '../Sparkline';

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function overdueLabel(marker) {
  if (marker.windowDays == null) return 'Not carried forward';
  const overdueBy = marker.ageDays - marker.windowDays;
  return overdueBy > 0 ? `${overdueBy}d past its ${marker.windowDays}d window` : `Within ${marker.windowDays}d window`;
}

// neverTested from the API is a flat list of canonical codes (e.g.
// "CARD.APOB", "HAE.FERR") — no display name, since a marker with zero
// observations has no observation to carry a name on. Grouped by the domain
// prefix (text before the first dot) so the list reads as "by body system"
// rather than an alphabetic wall of codes.
function groupByDomain(codes) {
  const groups = {};
  for (const code of codes) {
    const domain = code.split('.')[0] || code;
    (groups[domain] = groups[domain] || []).push(code);
  }
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

const REASON_LABEL = { STALE: 'Retest', NEVER_TESTED: 'Baseline needed' };
const REASON_BADGE = { STALE: 'badge-moderate', NEVER_TESTED: 'badge-unknown' };

// BiomarkerDefinition.domain (backend) is one of a fixed set — MET, CARD,
// LIV, REN, NUT, THYR, HAE, SLP, SARC, ONC — used here only to group the
// patient view's Current tab into readable section headers. Not the same
// thing as groupByDomain() above, which groups NEVER_TESTED's bare
// canonical-code prefixes (no domain field exists for a code with zero
// observations); this reads the real domain the backend already resolved
// for every fresh/recommended marker.
const DOMAIN_LABELS = {
  MET:  'Metabolic',
  CARD: 'Cardiovascular',
  LIV:  'Liver Function',
  REN:  'Kidney Function',
  NUT:  'Nutrition',
  THYR: 'Thyroid',
  HAE:  'Haematology',
  SLP:  'Sleep',
  SARC: 'Muscle & Strength',
  ONC:  'Oncology',
};

function domainLabel(domain) {
  return DOMAIN_LABELS[domain] || domain || 'Other';
}

// markerStatus.js's badge classes, mapped to a plain color for the small
// status dot on each patient-view marker row — same clinical-risk
// vocabulary (badge-low/confirmed/moderate/critical/unknown), just a dot
// instead of a filled pill so it reads as a quick scan cue beside the name
// rather than competing with the value's own badge.
const STATUS_DOT_COLOR = {
  'badge-low':       'var(--green)',
  'badge-confirmed': 'var(--blue)',
  'badge-moderate':  'var(--amber)',
  'badge-critical':  'var(--red)',
  'badge-unknown':   'var(--text-muted)',
};

function groupMarkersByDomain(markers) {
  const groups = {};
  for (const m of markers) {
    const domain = m.domain || 'OTHER';
    (groups[domain] = groups[domain] || []).push(m);
  }
  return Object.entries(groups).sort(([a], [b]) => domainLabel(a).localeCompare(domainLabel(b)));
}

function matchesSearch(query, ...fields) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return fields.some(f => (f || '').toLowerCase().includes(q));
}

// Fresh-marker statuses that are not a finding — everything else counts
// toward "Needs attention" (elevated, low, high, critical, unknown).
const IN_RANGE_STATUSES = new Set(['normal', 'optimal']);
const needsAttention = (m) => !IN_RANGE_STATUSES.has((m.status || '').toLowerCase());

// Glyph per BiomarkerDefinition.domain for the health-areas rail. Icon falls
// back to a neutral glyph for anything not listed.
const DOMAIN_ICONS = {
  MET: 'bolt', CARD: 'heart', LIV: 'vial', REN: 'drop', NUT: 'pill',
  THYR: 'compass', HAE: 'drop', SLP: 'clock', SARC: 'trend', ONC: 'stethoscope',
};

/** Recommended-tab card, patient view. */
function PatientRecommendationCard({ rec }) {
  return (
    <div className="mk-card mk-card--rec">
      <div className="mk-card__row">
        <div className="mk-card__id">
          <div className="mk-card__name">{rec.markerName || rec.canonicalCode}</div>
          <div className="mk-card__code">{rec.canonicalCode}</div>
        </div>
        <span className={`badge ${REASON_BADGE[rec.reason] || 'badge-unknown'}`}>
          {REASON_LABEL[rec.reason] || rec.reason}
        </span>
      </div>
      <div className="mk-card__detail">{rec.detail}</div>
    </div>
  );
}

// A clicked marker's trend, inline below its card — the color follows the
// LATEST draw's own status (from markerHistory, not the effective-panel
// status badge above it) so it reads the same as the old Health Story
// "Marker trends" section this replaced.
// Hover text for a trend dot: draw date + value.
const drawLabel = (p) => {
  const d = p.date ? new Date(p.date) : null;
  const when = d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Draw date unknown';
  return `${when} · ${p.val} ${p.unit || ''}`.trim();
};

function MarkerTrendPanel({ points, uid }) {
  const vals   = points.map(p => p.val);
  const latest = points[points.length - 1];
  const color = latest.status === 'critical' ? 'var(--red)'
    : latest.status === 'elevated' || latest.status === 'low' ? 'var(--amber)'
    : 'var(--green)';
  return (
    <div className="mk-trend">
      <Sparkline points={vals} color={color} width={260} height={40} uid={uid} labels={points.map(drawLabel)} />
      <div className="mk-trend__range">
        <span>{points[0].val} {points[0].unit}</span>
        <span style={{ color, fontWeight: 500 }}>→ {latest.val} {latest.unit}</span>
      </div>
      <div className="mk-trend__count">
        {points.length} draw{points.length !== 1 ? 's' : ''} with this marker
      </div>
    </div>
  );
}

/**
 * Current-tab card, patient view. Clickable when markerHistory has a
 * matching series (matchMarkerHistory) for this marker: tapping it expands
 * that marker's own trend below the row, replacing the old Health Story
 * "Marker trends" section's top-6 pick with "click the marker you care
 * about" instead.
 */
function PatientMarkerCard({ marker, markerHistory }) {
  const [expanded, setExpanded] = useState(false);
  const badgeClass = markerStatusBadgeClass(marker.status);
  const dotColor = STATUS_DOT_COLOR[badgeClass] || 'var(--text-muted)';
  const trendPoints = matchMarkerHistory(markerHistory, marker.markerName, marker.canonicalCode);
  const hasTrend = trendPoints && trendPoints.length >= 2;
  const toggle = () => setExpanded(v => !v);
  const name = marker.markerName || marker.canonicalCode;

  return (
    <div
      className={`mk-card${hasTrend ? ' mk-card--clickable' : ''}`}
      role={hasTrend ? 'button' : undefined}
      tabIndex={hasTrend ? 0 : undefined}
      aria-expanded={hasTrend ? expanded : undefined}
      aria-label={hasTrend ? `${name} trend` : undefined}
      onClick={hasTrend ? toggle : undefined}
      onKeyDown={hasTrend ? (e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      }) : undefined}
    >
      <div className="mk-card__row">
        <span className="mk-dot" aria-hidden="true" style={{ background: dotColor }} />
        <div className="mk-card__id">
          <div className="mk-card__name" title={name}>{name}</div>
          <div className="mk-card__code">{marker.canonicalCode}</div>
        </div>
        <div className="mk-card__result">
          <div className="mk-card__value">
            {marker.valueText || '—'}
            {marker.unit && <span className="mk-card__unit"> {marker.unit}</span>}
          </div>
          <span className={`badge ${badgeClass}`}>{markerStatusLabel(marker.status)}</span>
        </div>
        {hasTrend && (
          <Icon name="chevronRight" size={14} className={`mk-card__chev${expanded ? ' is-open' : ''}`} />
        )}
      </div>
      {expanded && hasTrend && (
        <MarkerTrendPanel
          points={trendPoints}
          uid={(marker.canonicalCode || marker.markerName || 'marker').replace(/[^a-zA-Z0-9]/g, '')}
        />
      )}
    </div>
  );
}

/**
 * The patient portal's own rendering of the Current/Recommended split: a
 * stat strip, one toolbar (segmented Current/Recommended, search, "Needs
 * attention" filter), then a health-areas rail beside a two-column grid of
 * marker cards for the selected area. On phones the rail becomes a
 * horizontally scrolling selector and the grid a single column. The
 * navigator/doctor view's own JSX (the return path below) is untouched.
 */
function PatientMarkersView({ panel, activeTab, setActiveTab, hasAnyData, markerHistory }) {
  const [search, setSearch] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [areaKey, setAreaKey] = useState(null);

  const recommendations = panel.recommendations || [];
  const attentionCount = panel.fresh.filter(needsAttention).length;
  const areaCount = new Set(panel.fresh.map(m => m.domain || 'OTHER')).size;
  const isCurrent = activeTab === 'Current';

  const items = isCurrent
    ? panel.fresh.filter(m => (!attentionOnly || needsAttention(m))
        && matchesSearch(search, m.markerName, m.canonicalCode))
    : recommendations.filter(r => matchesSearch(search, r.markerName, r.canonicalCode));
  const groups = groupMarkersByDomain(items);
  const [selectedKey, selectedItems] = groups.find(([k]) => k === areaKey) || groups[0] || [];
  const updated = isCurrent && selectedItems
    ? selectedItems.map(m => m.drawDate).filter(Boolean).sort().pop()
    : null;
  const unfiltered = isCurrent ? panel.fresh.length : recommendations.length;

  return (
    <div>
      <div className="mk-panel">
        <div className="mk-toolbar">
          <div className="mk-seg" role="tablist">
            {PATIENT_TABS.map(t => (
              <button
                key={t} type="button" role="tab"
                aria-selected={activeTab === t}
                className={`mk-seg__btn${activeTab === t ? ' active' : ''}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
                <span className="mk-count">{t === 'Current' ? panel.fresh.length : recommendations.length}</span>
              </button>
            ))}
          </div>

          {hasAnyData && (
            <>
              <div className="mk-search">
                <Icon name="search" size={15} className="mk-search__icon" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search all markers"
                  aria-label="Search markers"
                  className="mk-search__input"
                />
              </div>
              {isCurrent && (
                <button
                  type="button"
                  className={`mk-chip${attentionOnly ? ' active' : ''}`}
                  aria-pressed={attentionOnly}
                  onClick={() => setAttentionOnly(v => !v)}
                >
                  <span className="mk-dot" aria-hidden="true" style={{ background: 'var(--amber)' }} />
                  Needs attention
                  <span className="mk-count">{attentionCount}</span>
                </button>
              )}
            </>
          )}
        </div>

        {!hasAnyData ? (
          <div className="empty-state">
            <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
            <div className="empty-title">No lab data yet</div>
            <div className="empty-desc">No blood markers have been recorded yet.</div>
          </div>
        ) : groups.length === 0 ? (
          unfiltered > 0 ? (
            <div className="mk-none">
              {search.trim() ? `No markers match "${search}".` : 'No markers need attention right now.'}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
              <div className="empty-title">{isCurrent ? 'No current markers yet' : 'Nothing to retest right now'}</div>
              <div className="empty-desc">
                {isCurrent
                  ? 'Nothing on file is within its valid window right now.'
                  : "You're up to date — nothing on this list is aged out or overdue."}
              </div>
            </div>
          )
        ) : (
          <div className="mk-body">
            <nav className="mk-rail" aria-label="Health areas">
              <div className="mk-rail__head">
                <span>Health areas</span>
                <span className="mk-count">{groups.length}</span>
              </div>
              {groups.map(([key, list]) => {
                const attn = isCurrent ? list.filter(needsAttention).length : 0;
                return (
                  <button
                    key={key} type="button"
                    className={`mk-area${key === selectedKey ? ' active' : ''}`}
                    aria-current={key === selectedKey ? 'true' : undefined}
                    onClick={() => setAreaKey(key)}
                  >
                    <span className="mk-area__icon"><Icon name={DOMAIN_ICONS[key] || 'inbox'} size={16} /></span>
                    <span className="mk-area__name">{domainLabel(key)}</span>
                    {attn > 0 && <span className="mk-area__attn">{attn}</span>}
                    <span className="mk-area__total">{list.length}</span>
                  </button>
                );
              })}
            </nav>

            <section className="mk-main" aria-label={domainLabel(selectedKey)}>
              <header className="mk-main__head">
                <span className="mk-area__icon mk-area__icon--lg">
                  <Icon name={DOMAIN_ICONS[selectedKey] || 'inbox'} size={20} />
                </span>
                <div className="mk-main__titles">
                  <h2 className="mk-main__title">{domainLabel(selectedKey)}</h2>
                  <div className="mk-main__sub">
                    {selectedItems.length} marker{selectedItems.length !== 1 ? 's' : ''}
                    {isCurrent ? ' · Latest results across all reports' : ' · Worth retesting on your next visit'}
                  </div>
                </div>
                {updated && <div className="mk-main__updated">Updated {fmtDate(updated)}</div>}
              </header>
              <div className="mk-grid">
                {isCurrent
                  ? selectedItems.map(m => (
                      <PatientMarkerCard key={m.canonicalCode} marker={m} markerHistory={markerHistory} />
                    ))
                  : selectedItems.map(r => <PatientRecommendationCard key={r.canonicalCode} rec={r} />)}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function RecommendationRow({ rec }) {
  return (
    <tr>
      <td data-label="Marker">
        <strong>{rec.markerName || rec.canonicalCode}</strong>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{rec.canonicalCode}</div>
      </td>
      <td data-label="Reason">
        <span className={`badge ${REASON_BADGE[rec.reason] || 'badge-unknown'}`}>
          {REASON_LABEL[rec.reason] || rec.reason}
        </span>
      </td>
      <td data-label="Detail">
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{rec.detail}</span>
      </td>
    </tr>
  );
}

function MarkerRow({ marker, showOverdue }) {
  return (
    <tr>
      <td data-label="Marker">
        <strong>{marker.markerName || marker.canonicalCode}</strong>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{marker.canonicalCode}</div>
      </td>
      <td data-label="Value">{marker.valueText || '—'}{marker.unit ? ` ${marker.unit}` : ''}</td>
      <td data-label="Status">
        <span className={`badge ${markerStatusBadgeClass(marker.status)}`}>
          {markerStatusLabel(marker.status)}
        </span>
      </td>
      <td data-label="Drawn">
        {fmtDate(marker.drawDate)}
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {marker.ageDays != null ? `${marker.ageDays}d ago` : ''}
        </div>
      </td>
      {showOverdue && (
        <td data-label="Window">
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{overdueLabel(marker)}</span>
        </td>
      )}
    </tr>
  );
}

const PATIENT_TABS = ['Current', 'Recommended'];

export default function EffectivePanelTab({ patient, patientView = false, markerHistory, onLoaded }) {
  const [panel, setPanel]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  // Only meaningful when patientView — the navigator/doctor view still
  // shows every section at once, same as before.
  const [activeTab, setActiveTab] = useState('Current');
  // Navigator/doctor view: Current Markers / Recommended / Stale / Never Tested.
  const [navTab, setNavTab] = useState('Current');

  const load = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await effectivePanelAPI.get(patient.id);
      setPanel(res.data);
      if (onLoaded) {
        const fresh = res.data.fresh || [];
        onLoaded({
          current: fresh.length,
          attention: fresh.filter(needsAttention).length,
          areas: new Set(fresh.map(m => m.domain || 'OTHER')).size,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load current markers.');
    } finally {
      setLoading(false);
    }
  }, [patient?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- onLoaded is a setter; re-fetching on its identity would loop

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="loading-state"><div className="spinner" />Loading current markers…</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="alert" size={20} /></div>
          <div className="empty-title">Couldn't load current markers</div>
          <div className="empty-desc">{error}</div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const hasAnyData = panel.fresh.length > 0 || panel.stale.length > 0;

  // The patient portal gets its own rendering entirely (segmented pill tabs,
  // search, grouped card rows) — see PatientMarkersView's own header
  // comment. Everything below this belongs to the navigator/doctor view,
  // unchanged.
  if (patientView) {
    return (
      <PatientMarkersView
        panel={panel}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        hasAnyData={hasAnyData}
        markerHistory={markerHistory}
      />
    );
  }

  const neverTestedGroups = groupByDomain(panel.neverTested || []);
  const recommendations = panel.recommendations || [];
  const tabs = [
    { key: 'Current',     label: 'Current Markers', count: panel.fresh.length },
    { key: 'Recommended', label: 'Recommended',     count: recommendations.length },
    { key: 'Stale',       label: 'Stale',           count: panel.stale.length },
    { key: 'NeverTested', label: 'Never Tested',    count: panel.neverTested.length },
  ];

  return (
    <div>
      <div className="tabs-bar" role="tablist" style={{ marginBottom: 18 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={navTab === t.key}
            className={`tab-btn${navTab === t.key ? ' active' : ''}`}
            onClick={() => setNavTab(t.key)}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {!hasAnyData && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
            <div className="empty-title">No lab data yet</div>
            <div className="empty-desc">This patient hasn't had any blood markers recorded.</div>
          </div>
        </div>
      )}

      {navTab === 'Recommended' && (
        recommendations.length > 0 ? (
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-title">Recommended Retests ({recommendations.length})</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
              Worth ordering on the next visit — either aged out of their retest window or never
              drawn for this patient. Excludes acute markers and anything without a clinically
              reviewed retest window, so this list is deliberately narrower than Stale + Never Tested.
            </div>
            <table className="stack-table">
              <thead><tr><th>Marker</th><th>Reason</th><th>Detail</th></tr></thead>
              <tbody>
                {recommendations.map(r => <RecommendationRow key={r.canonicalCode} rec={r} />)}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
              <div className="empty-title">Nothing to retest right now</div>
              <div className="empty-desc">Nothing on this list is aged out or overdue.</div>
            </div>
          </div>
        )
      )}

      {navTab === 'Current' && hasAnyData && (
        <>
          {panel.fresh.length > 0 ? (
            <div className="card" style={{ marginBottom: 18 }}>
              <div className="card-title"><Icon name="check" size={14} /> Current ({panel.fresh.length})</div>
              <table className="stack-table">
                <thead><tr><th>Marker</th><th>Value</th><th>Status</th><th>Drawn</th></tr></thead>
                <tbody>
                  {panel.fresh.map(m => <MarkerRow key={m.canonicalCode} marker={m} />)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 18 }}>
              <div className="empty-state">
                <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
                <div className="empty-title">No current markers yet</div>
                <div className="empty-desc">Nothing on file is within its valid window right now.</div>
              </div>
            </div>
          )}

        </>
      )}

      {navTab === 'Stale' && hasAnyData && (
        panel.stale.length > 0 ? (
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-title">Stale — on file, but aged out ({panel.stale.length})</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12 }}>
              A real value exists for these — it's just old enough that it may no longer reflect
              the patient's current state. Shown here rather than hidden, so "no current data" and
              "no data at all" don't look the same.
            </div>
            <table className="stack-table">
              <thead><tr><th>Marker</th><th>Value</th><th>Status</th><th>Drawn</th><th>Window</th></tr></thead>
              <tbody>
                {panel.stale.map(m => <MarkerRow key={m.canonicalCode} marker={m} showOverdue />)}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
              <div className="empty-title">No stale markers</div>
              <div className="empty-desc">Nothing on file has aged out of its valid window.</div>
            </div>
          </div>
        )
      )}

      {navTab === 'NeverTested' && (
        neverTestedGroups.length > 0 ? (
          <div className="card">
            <div className="card-title">◌ Never Tested ({panel.neverTested.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {neverTestedGroups.map(([domain, codes]) => (
                <div key={domain}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                    {domain}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {codes.map(code => (
                      <span key={code} className="badge badge-unknown">{code}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="vial" size={20} /></div>
              <div className="empty-title">Every marker has been tested</div>
              <div className="empty-desc">No marker is missing a baseline for this patient.</div>
            </div>
          </div>
        )
      )}
    </div>
  );
}
